import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  fetchPlatformProductFeedback,
  fetchPlatformProductFeedbackDetail,
  requestProductFeedbackScreenshotSignedUrl,
  type PlatformProductFeedbackDetail,
} from "../data/product-feedback-staff";
import type { ProductFeedbackSystemInfoKey } from "../data/product-feedback";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";

const SYSTEM_INFO_LABELS: ReadonlyArray<{
  key: ProductFeedbackSystemInfoKey;
  label: string;
}> = [
  { key: "app_build", label: "App & build" },
  { key: "browser_device", label: "Browser & device" },
  { key: "page", label: "Where they were" },
  { key: "account_role", label: "Account & role" },
  { key: "session", label: "Session" },
  { key: "error_codes", label: "Recent technical errors" },
];

type ScreenshotReadState =
  | { readonly status: "idle" }
  | { readonly status: "loading"; readonly feedbackId: string }
  | { readonly status: "ready"; readonly feedbackId: string; readonly url: string }
  | { readonly status: "error"; readonly feedbackId: string; readonly reason: string };

function systemInfoValue(
  detail: PlatformProductFeedbackDetail,
  key: ProductFeedbackSystemInfoKey,
): string | null {
  const value = detail.systemInfo[key];
  if (value === undefined) return null;
  if (Array.isArray(value)) {
    return value.length === 0 ? "None captured in this tab" : value.join(" · ");
  }
  return String(value);
}

export function PlatformFeedback({
  client,
  timeZone,
  pollMs,
}: {
  client: SupabaseClient;
  timeZone: string;
  pollMs: number;
}) {
  const list = useReadModel(() => fetchPlatformProductFeedback(client), [client], pollMs);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const detail = useReadModel(
    () =>
      selectedId === null
        ? Promise.resolve<PlatformProductFeedbackDetail | null>(null)
        : fetchPlatformProductFeedbackDetail(client, selectedId),
    [client, selectedId],
  );
  const [screenshot, setScreenshot] = useState<ScreenshotReadState>({ status: "idle" });

  const rows = list.data ?? [];

  useEffect(() => {
    if (selectedId === null && rows[0] !== undefined) setSelectedId(rows[0].feedbackId);
  }, [rows, selectedId]);

  useEffect(() => setScreenshot({ status: "idle" }), [selectedId]);

  async function openScreenshot(feedbackId: string): Promise<void> {
    setScreenshot({ status: "loading", feedbackId });
    try {
      const url = await requestProductFeedbackScreenshotSignedUrl(client, feedbackId);
      setScreenshot({ status: "ready", feedbackId, url });
    } catch (error) {
      setScreenshot({
        status: "error",
        feedbackId,
        reason:
          error instanceof Error
            ? error.message
            : "Could not open this private screenshot.",
      });
    }
  }

  if (list.status === "loading") {
    return <p className="page__note">Loading product feedback…</p>;
  }
  if (list.status === "error") {
    return <p className="error">{list.error ?? "Could not load product feedback."}</p>;
  }
  if (rows.length === 0) {
    return (
      <div className="pfd-queue-empty">
        <span className="pfd-queue-empty__check" aria-hidden="true">✓</span>
        <h3>No feedback yet.</h3>
        <p>New problems and ideas from every account will appear here after intake succeeds.</p>
      </div>
    );
  }

  const selectedDetail = detail.data;
  const selectedScreenshot =
    selectedDetail !== null &&
    screenshot.status !== "idle" &&
    screenshot.feedbackId === selectedDetail.feedbackId
      ? screenshot
      : { status: "idle" as const };

  return (
    <div className="pff-layout">
      <div className="pff-list" aria-label="Product feedback list">
        <table className="pfd-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Account</th>
              <th>Kind</th>
              <th>Report</th>
              <th>Context</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.feedbackId} className={selectedId === row.feedbackId ? "is-selected" : undefined}>
                <td className="pfd-age">{formatDisplayTime(row.createdAt, timeZone)}</td>
                <td className="pfd-co">
                  {row.accountNameSnapshot}
                  {row.sourceArchived ? <small>Archived source</small> : null}
                </td>
                <td><span className={`pff-kind pff-kind--${row.type}`}>{row.type === "idea" ? "Idea" : "Problem"}</span></td>
                <td>
                  <button
                    type="button"
                    className="pff-select"
                    aria-pressed={selectedId === row.feedbackId}
                    onClick={() => setSelectedId(row.feedbackId)}
                  >
                    {row.preview}
                  </button>
                </td>
                <td className="pff-context">
                  {row.appBuild ?? "No build"}
                  {row.hasScreenshot ? <small>Screenshot attached</small> : null}
                  {row.allowContactAtSubmission ? (
                    <small>Contact allowed at submission</small>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <aside className="pff-detail" aria-label="Product feedback detail">
        {detail.status === "error" ? (
          <p className="error">{detail.error ?? "Could not load this feedback report."}</p>
        ) : detail.status === "loading" || selectedDetail === null ? (
          <p className="page__note">Loading report…</p>
        ) : (
          <>
            <div className="pff-detail__head">
              <span className={`pff-kind pff-kind--${selectedDetail.type}`}>
                {selectedDetail.type === "idea" ? "Idea" : "Problem"}
              </span>
              <time>{formatDisplayTime(selectedDetail.createdAt, timeZone)}</time>
              <h3>{selectedDetail.title ?? selectedDetail.accountNameSnapshot}</h3>
              <p>{selectedDetail.accountNameSnapshot}</p>
            </div>

            {selectedDetail.sourceArchived ? (
              <section className="pff-detail__section" aria-label="Archived feedback source">
                <h4>Historical source</h4>
                <p className="page__note">
                  {selectedDetail.accountSourceDeleted && selectedDetail.submitterSourceDeleted
                    ? "The source account and submitter user were deleted. "
                    : selectedDetail.accountSourceDeleted
                      ? "The source account was deleted. "
                      : "The submitter user was deleted. "}
                  Account and reporter identity below are immutable snapshots recorded when this
                  feedback was submitted.
                </p>
              </section>
            ) : null}

            <section className="pff-detail__section">
              <h4>Reporter</h4>
              <p>
                {selectedDetail.reporterNameSnapshot}
                {selectedDetail.reporterEmailSnapshot === null ? null : (
                  <small>{selectedDetail.reporterEmailSnapshot}</small>
                )}
              </p>
              <span className="pff-contact">
                Contact choice recorded at submission: {selectedDetail.allowContactAtSubmission
                  ? "allowed."
                  : "not allowed."}
                {selectedDetail.sourceArchived
                  ? " This historical choice is not current consent."
                  : null}
              </span>
            </section>

            <section className="pff-detail__section">
              <h4>Report</h4>
              {selectedDetail.problemKinds.length > 0 ? (
                <div className="pff-kinds">
                  {selectedDetail.problemKinds.map((kind) => <span key={kind}>{kind}</span>)}
                </div>
              ) : null}
              <p className="pff-message">
                {selectedDetail.message ?? "No written note — context or screenshot only."}
              </p>
            </section>

            <section className="pff-detail__section">
              <h4>Screenshot</h4>
              {!selectedDetail.hasScreenshot ? (
                <p className="page__note">No screenshot attached.</p>
              ) : (
                <div className="pff-screenshot">
                  <button
                    type="button"
                    disabled={selectedScreenshot.status === "loading"}
                    onClick={() => void openScreenshot(selectedDetail.feedbackId)}
                  >
                    {selectedScreenshot.status === "loading"
                      ? "Opening…"
                      : "View private screenshot"}
                  </button>
                  <small>
                    Signed access expires after 60 seconds
                    {selectedDetail.screenshotSizeBytes === null
                      ? ""
                      : ` · ${Math.ceil(selectedDetail.screenshotSizeBytes / 1024)} KB`}
                  </small>
                  {selectedScreenshot.status === "ready" ? (
                    <img src={selectedScreenshot.url} alt="Screenshot attached by the reporter" />
                  ) : null}
                  {selectedScreenshot.status === "error" ? (
                    <p className="error" role="alert">{selectedScreenshot.reason}</p>
                  ) : null}
                </div>
              )}
            </section>

            <section className="pff-detail__section">
              <h4>System info</h4>
              <dl className="pff-system">
                {SYSTEM_INFO_LABELS.map(({ key, label }) => {
                  const value = systemInfoValue(selectedDetail, key);
                  return value === null ? null : (
                    <div key={key}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  );
                })}
              </dl>
              <p className="pff-privacy">
                System info excludes passwords, tokens, precise location, error messages, stacks,
                and request/response bodies. Recent errors contain names/codes only; reporters
                separately review screenshots before sending.
              </p>
            </section>
          </>
        )}
      </aside>
    </div>
  );
}
